import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, FileCheck, DollarSign, CheckSquare, MessageCircle, AlertTriangle, Lightbulb, ArrowRight, Download, LogOut, CreditCard, Users, Ruler, Flag, CalendarCheck, BarChart3, Share2, Loader2, Rocket, Package2, Armchair, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppContext } from "@/lib/onboardingContext";
import { useAuth } from "@/lib/authContext";
import { useSubscription } from "@/lib/subscriptionContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { generateBusinessPlanPDF } from "@/lib/pdfExport";
import { toast } from "sonner";
import UpsellBanner from "@/components/UpsellBanner";
import UpgradeModal from "@/components/UpgradeModal";
import FeatureUpgradeModal from "@/components/FeatureUpgradeModal";
import { Lock } from "lucide-react";
import type { PlanType } from "@/lib/subscriptionContext";
import PlanBadge from "@/components/PlanBadge";
import LockedSection from "@/components/LockedSection";
import FeatureGate from "@/components/FeatureGate";
import ModeSwitch, { AppMode } from "@/components/ModeSwitch";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle } from "lucide-react";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  );
}
import { formatCityName } from "@/lib/utils";
import { formatAreaRange } from "@/lib/areaUnit";
import { useI18n } from "@/lib/i18nContext";
import { budgetRanges, OnboardingData, generateMockResults } from "@/lib/businessData";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { applyNeighborhoodToStartupTotal } from "@/lib/neighborhoodRent";
import PostOpeningDashboard from "@/components/post-opening/PostOpeningDashboard";
import SurvivalScoreCard from "@/components/SurvivalScoreCard";
import StaffingControl from "@/components/StaffingControl";
import { calculateSeatingCapacity, getSeatingLabel } from "@/lib/seatingCapacity";
import MyLocations from "@/pages/MyLocations";
import BeginnerTooltip, { useBeginnerText } from "@/components/BeginnerTooltip";
import FeedbackButton from "@/components/FeedbackButton";
import TeamButton from "@/components/TeamButton";

function getUnderestimatedItems(city: string, t: (k: string) => string) {
  const cs = getCurrencySymbol(city);
  const c = (usd: number) => convertFromUSD(usd, city).toLocaleString();
  return [
    { item: t("dash.permitFees"), detail: `First-time founders often miss ${cs}${c(3000)}–${cs}${c(8000)} in local, state, and health permits` },
    { item: t("dash.preOpenPayroll"), detail: `Staff training before opening typically runs 4–6 weeks of full payroll (~${cs}${c(18000)}–${cs}${c(27000)})` },
    { item: t("dash.contingencyBuffer"), detail: "Unexpected costs like HVAC repairs, code violations, or supply delays add up fast" },
  ];
}

function BudgetRealityCheck({ onboarding, realisticTotal, t }: { onboarding: OnboardingData; realisticTotal: number; t: (k: string, v?: Record<string, string>) => string }) {
  const userBudgetRange = budgetRanges.find(b => b.id === onboarding.budget);
  if (!userBudgetRange) return null;

  const userMax = userBudgetRange.value;
  const realisticMin = Math.round(realisticTotal * 0.85);

  if (userMax >= realisticMin) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8 rounded-xl border-2 border-accent/30 bg-accent/5 p-5"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-heading font-medium text-foreground text-sm">
            {t("dash.budgetReality")}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t("dash.budgetTight", { label: userBudgetRange.label, min: `${getCurrencySymbol(onboarding.city)}${convertFromUSD(realisticMin, onboarding.city).toLocaleString()}` })}
          </p>
          <div className="mt-3 space-y-2">
            {getUnderestimatedItems(onboarding.city, t).map((u) => (
              <div key={u.item} className="flex items-start gap-2 text-sm">
                <span className="text-accent font-medium shrink-0">•</span>
                <div>
                  <span className="font-medium text-foreground">{u.item}:</span>{" "}
                  <span className="text-muted-foreground">{u.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { results, beginnerMode, setBeginnerMode, onboarding, isOperationsUnlocked, selectedNeighborhood } = useAppContext();
  const { signOut, user } = useAuth();
  const { subscription, isWithinLimit, refreshSubscription } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const [featureModal, setFeatureModal] = useState<{ open: boolean; name: string; plan: Extract<PlanType, "pro" | "business"> }>({ open: false, name: "", plan: "pro" });
  const [downloading, setDownloading] = useState<string | null>(null);
  const [mode, setMode] = useState<AppMode>("opening");
  const [showLocations, setShowLocations] = useState(false);
  const { t } = useI18n();

  // Post-checkout confirmation modal state.
  type CheckoutConfirmation =
    | { kind: "success"; plan: string; status: string; billing_period: string;
        amount_total: number | null; currency: string | null;
        customer_email: string | null; session_id: string;
        current_period_end: string | null; }
    | { kind: "error"; message: string; session_id: string | null };
  const [checkoutConfirm, setCheckoutConfirm] = useState<CheckoutConfirmation | null>(null);
  // Auto-close the success modal after 5s. Toggle persists across opens.
  // Subscription state lives in context/DB and is unaffected by closing the modal.
  const [autoCloseConfirm, setAutoCloseConfirm] = useState(true);
  const [autoCloseRemaining, setAutoCloseRemaining] = useState(5);

  // Auto-redirect "managing" users to the operations dashboard. We check both
  // the in-memory onboarding goal (covers brand-new signups before the profile
  // round-trip) and the persisted profiles.goal (covers returning users on
  // other devices). Stays on /dashboard for the opening flow.
  useEffect(() => {
    // Allow explicit override via ?mode=opening (set when user clicks
    // "Switch to Opening Planner" from the operations dashboard). We persist
    // it in sessionStorage so subsequent navigations within the session
    // continue to honour the override.
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "opening") {
      sessionStorage.setItem("oplif-mode-override", "opening");
    }
    const override = sessionStorage.getItem("oplif-mode-override") === "opening";
    if (override) return;
    if (onboarding.goal === "managing") {
      navigate("/dashboard/operations", { replace: true });
      return;
    }
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("profiles")
        .select("goal")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!cancelled && data?.goal === "managing") {
        navigate("/dashboard/operations", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [user, onboarding.goal, navigate]);

  useEffect(() => {
    if (checkoutConfirm?.kind !== "success" || !autoCloseConfirm) return;
    setAutoCloseRemaining(5);
    const tick = setInterval(() => {
      setAutoCloseRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const timeout = setTimeout(() => {
      setCheckoutConfirm(null);
    }, 5000);
    return () => {
      clearInterval(tick);
      clearTimeout(timeout);
    };
  }, [checkoutConfirm, autoCloseConfirm]);

  // Handle return from Stripe Checkout: verify session, show confirmation modal,
  // refresh sub & clean URL.
  useEffect(() => {
    const upgrade = searchParams.get("upgrade");
    if (!upgrade) return;
    if (upgrade === "success") {
      const sessionId = searchParams.get("session_id");
      // Server-side fallback: verify the Checkout session and apply the
      // subscription immediately, even if the webhook hasn't fired.
      (async () => {
        if (sessionId) {
          try {
            const { data, error } = await supabase.functions.invoke("verify-checkout-session", {
              body: { session_id: sessionId },
            });
            if (error || !data?.ok) {
              setCheckoutConfirm({
                kind: "error",
                message: (data?.error as string) || error?.message || "Could not verify the Checkout session.",
                session_id: sessionId,
              });
            } else {
              setCheckoutConfirm({
                kind: "success",
                plan: data.plan,
                status: data.status,
                billing_period: data.billing_period,
                amount_total: data.amount_total ?? null,
                currency: data.currency ?? null,
                customer_email: data.customer_email ?? null,
                session_id: data.session_id ?? sessionId,
                current_period_end: data.current_period_end ?? null,
              });
            }
          } catch (err) {
            console.error("verify-checkout-session failed", err);
            setCheckoutConfirm({
              kind: "error",
              message: err instanceof Error ? err.message : "Verification request failed.",
              session_id: sessionId,
            });
          }
        } else {
          setCheckoutConfirm({
            kind: "error",
            message: "Returned from Checkout without a session_id — cannot confirm payment.",
            session_id: null,
          });
        }
        // Poll a couple of times so the UI reflects the new plan.
        let tries = 0;
        const tick = async () => {
          await refreshSubscription();
          tries += 1;
          if (tries < 4) setTimeout(tick, 1500);
        };
        tick();
      })();
    } else if (upgrade === "cancelled") {
      toast.info("Checkout cancelled — no charge was made.");
    }
    searchParams.delete("upgrade");
    searchParams.delete("plan");
    searchParams.delete("session_id");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refreshSubscription]);



  const plan = subscription?.plan ?? "free";
  const isFree = plan === "free";
  const isStudioPlus = plan === "business" || plan === "enterprise";

  // If no onboarding/results, render a generic sample so the dashboard
  // (and Gantt chart) is always populated. We do NOT persist this — only use
  // it for display when the user hasn't onboarded yet. Defaults intentionally
  // mirror the most common concept (restaurant) and a widely-supported city
  // (Miami) so the placeholder never contradicts what the user actually
  // selected on a different page.
  const sampleOnboarding: OnboardingData = {
    ...onboarding,
    businessName: onboarding.businessName || "Sample Restaurant",
    businessType: onboarding.businessType || "restaurant",
    city: onboarding.city || "miami",
    budget: onboarding.budget || "100k-250k",
    experience: onboarding.experience || "some",
    businessSize: onboarding.businessSize || "medium",
    locationType: onboarding.locationType || "permanent",
    launchSpeed: onboarding.launchSpeed || "balanced",
    serviceStyle: onboarding.serviceStyle || "full-service",
    teamSize: onboarding.teamSize || "full",
    seats: onboarding.seats || 60,
    indoorSeats: onboarding.indoorSeats || 40,
    outdoorSeats: onboarding.outdoorSeats || 20,
    barSeats: onboarding.barSeats || 0,
    sqftMin: onboarding.sqftMin || 1500,
    sqftMax: onboarding.sqftMax || 2500,
    daysPerWeek: onboarding.daysPerWeek || 7,
    goal: onboarding.goal || "opening",
  };
  const hasRealData = !!results || !!onboarding.businessName;
  const r = results ?? generateMockResults(sampleOnboarding);
  const bt = useBeginnerText();
  const scoreColor = r ? (r.openingScore >= 80 ? "text-primary" : r.openingScore >= 60 ? "text-accent" : "text-destructive") : "text-muted-foreground";
  const businessDisplayName = onboarding.businessName || r?.businessLabel || (hasRealData ? "Your Business" : "Sample Restaurant");

  const handleExport = async (lang: "en" | "es") => {
    if (!isWithinLimit("exports")) { setUpgradeFeature("PDF Exports"); setUpgradeOpen(true); return; }
    setDownloading(lang);
    try {
      await new Promise(r => setTimeout(r, 100));
      generateBusinessPlanPDF(results, lang, onboarding.businessType);
      if (user) {
        await supabase.from("pdf_downloads").insert({
          user_id: user.id, language: lang, business_type: results.businessLabel, city: results.city,
        });
      }
      toast.success(lang === "en" ? "PDF downloaded!" : "¡PDF descargado!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handleSignOut = async () => { await signOut(); navigate("/"); };

  const cs = r?.currencySymbol || "$";
  const fmt = (n: number) => n >= 1000 ? `${cs}${(n / 1000).toFixed(0)}K` : `${cs}${n}`;

  // Neighborhood-driven dynamic startup total. When a neighborhood is selected
  // for the active city, replace the static "Deposits & Rent" line with one
  // driven by midpoint $/sqft/yr × venue size, so the headline figure
  // reflects the chosen location's true rent burden.
  const cityKey = (onboarding.city || "").toLowerCase().trim();
  const activeNeighborhood = selectedNeighborhood?.cityKey === cityKey ? selectedNeighborhood : null;
  const dynamicTotalEstimate = (() => {
    if (!r) return 0;
    if (!activeNeighborhood) return r.totalEstimate;
    const depositsLine = r.costBreakdown.find(c => /deposits|rent|lease/i.test(c.category))?.estimate ?? 0;
    // monthlyRentLocal is in local currency; r.totalEstimate is in USD.
    // Derive the city's USD→local rate via convertFromUSD(1, city).
    const rate = convertFromUSD(1, cityKey) || 1;
    const monthlyRentUSD = activeNeighborhood.monthlyRentLocal / rate;
    return applyNeighborhoodToStartupTotal({
      baseTotal: r.totalEstimate,
      oldDepositsLine: depositsLine,
      selected: { ...activeNeighborhood, monthlyRentLocal: monthlyRentUSD },
    });
  })();

  return (
    <div className="min-h-screen bg-background pb-16">
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} feature={upgradeFeature} requiredPlan="Pro" />

      {/* Post-Checkout confirmation modal — shows the verified plan, amount,
          and Stripe session ID so we can confirm the charge & redirect. */}
      <Dialog open={!!checkoutConfirm} onOpenChange={(o) => { if (!o) setCheckoutConfirm(null); }}>
        <DialogContent className="sm:max-w-md">
          {checkoutConfirm?.kind === "success" ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <DialogTitle>Payment confirmed</DialogTitle>
                </div>
                <DialogDescription>
                  Your subscription is active and you've been redirected to the dashboard.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 rounded-lg border border-border bg-muted/40 p-4 text-sm space-y-2">
                <Row label="Plan" value={<span className="font-medium capitalize">{checkoutConfirm.plan}</span>} />
                <Row label="Billing" value={<span className="capitalize">{checkoutConfirm.billing_period}</span>} />
                <Row label="Status" value={<span className="capitalize">{checkoutConfirm.status}</span>} />
                {checkoutConfirm.amount_total != null && (
                  <Row
                    label="Amount charged"
                    value={
                      <span className="font-medium">
                        {(checkoutConfirm.amount_total / 100).toLocaleString(undefined, {
                          style: "currency",
                          currency: (checkoutConfirm.currency || "usd").toUpperCase(),
                        })}
                      </span>
                    }
                  />
                )}
                {checkoutConfirm.customer_email && (
                  <Row label="Receipt to" value={checkoutConfirm.customer_email} />
                )}
                {checkoutConfirm.current_period_end && (
                  <Row
                    label="Renews"
                    value={new Date(checkoutConfirm.current_period_end).toLocaleDateString()}
                  />
                )}
                <Row
                  label="Session ID"
                  value={<span className="font-mono text-xs break-all">{checkoutConfirm.session_id}</span>}
                />
                <Row label="Returned to" value={<span className="font-mono text-xs">/dashboard</span>} />
              </div>
            </>
          ) : checkoutConfirm?.kind === "error" ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-destructive" />
                  <DialogTitle>Could not confirm payment</DialogTitle>
                </div>
                <DialogDescription>
                  We couldn't verify the Checkout session automatically.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 rounded-lg border border-border bg-muted/40 p-4 text-sm space-y-2">
                <Row label="Error" value={<span className="text-destructive">{checkoutConfirm.message}</span>} />
                {checkoutConfirm.session_id && (
                  <Row
                    label="Session ID"
                    value={<span className="font-mono text-xs break-all">{checkoutConfirm.session_id}</span>}
                  />
                )}
              </div>
            </>
          ) : null}
          <DialogFooter className="sm:justify-between gap-3 flex-col sm:flex-row items-stretch sm:items-center">
            {checkoutConfirm?.kind === "success" ? (
              <label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={autoCloseConfirm}
                  onChange={(e) => setAutoCloseConfirm(e.target.checked)}
                />
                Auto-close in {autoCloseRemaining}s
              </label>
            ) : <span />}
            <Button onClick={() => setCheckoutConfirm(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FeatureUpgradeModal
        open={featureModal.open}
        onOpenChange={(o) => setFeatureModal((s) => ({ ...s, open: o }))}
        featureName={featureModal.name}
        requiredPlan={featureModal.plan}
      />

      {/* Header */}
      <div className="border-b border-border bg-card">
      <div className="container py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4 min-w-0">
            <span className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer shrink-0" onClick={() => navigate("/")}>
              oplif<span className="text-primary">.</span>
            </span>
            {/* ═══ MODE SWITCH — Opening Plan ↔ Operations ═══ */}
            <div className="hidden md:block">
              <ModeSwitch
                value={mode}
                onChange={(m) => { setMode(m); setShowLocations(false); }}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap justify-end">
            <PlanBadge plan={plan} />
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t("dash.beginnerMode")}</span>
              <Switch checked={beginnerMode} onCheckedChange={setBeginnerMode} />
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/assistant")} className="gap-1.5 h-8 px-2 sm:px-3">
              <MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">{t("dash.askAI")}</span>
            </Button>
            <FeedbackButton />
            <TeamButton />
            <Button size="sm" variant="outline" onClick={() => navigate("/billing")} className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:gap-1"><CreditCard className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={handleSignOut} className="h-8 w-8 p-0 text-muted-foreground"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
        {/* Mobile mode switch */}
        <div className="md:hidden container pb-3">
          <ModeSwitch
            value={mode}
            onChange={(m) => { setMode(m); setShowLocations(false); }}
          />
        </div>
      </div>

      <div className="container pt-8">
        {/* Upsell Banner */}
        <div className="mb-6">
          <UpsellBanner />
        </div>

        <Tabs value={showLocations ? "locations" : mode} onValueChange={(v) => {
          if (v === "locations") setShowLocations(true);
          else { setShowLocations(false); setMode(v as AppMode); }
        }} className="w-full">
          {/* Secondary nav: Locations link */}
          <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm pb-4 pt-1 -mt-1 flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {showLocations ? (
                <span className="font-medium text-foreground">My Locations</span>
              ) : mode === "opening" ? (
                <span><Flag className="inline w-3.5 h-3.5 mr-1.5 text-primary" />Opening Plan · permits, budget, timeline, location, staffing</span>
              ) : (
                <span><Rocket className="inline w-3.5 h-3.5 mr-1.5 text-primary" />Operations · revenue, P&L, inventory, staff rota, inspections, lease, benchmarks</span>
              )}
            </div>
            <Button
              variant={showLocations ? "default" : "outline"}
              size="sm"
              onClick={() => setShowLocations(s => !s)}
              className="gap-1.5 h-8 shrink-0"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{showLocations ? "Back to Dashboard" : "My Locations"}</span>
            </Button>
          </div>

          <TabsContent value="operations">
            <FeatureGate feature="operations" featureName="Operations Suite" inline>
              <PostOpeningDashboard />
            </FeatureGate>
          </TabsContent>

          <TabsContent value="locations">
            <FeatureGate feature="multiLocation" featureName="My Locations" inline>
              <MyLocations embedded />
            </FeatureGate>
          </TabsContent>

          <TabsContent value="opening">
        {r ? (
        <>
        {/* ═══ HERO: Business at a Glance ═══ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-foreground mb-1">
            {t("dash.title").replace("{business}", r.businessLabel).replace("{city}", formatCityName(r.city))}
          </h1>
          <p className="text-sm text-muted-foreground">{t("dash.subtitle")}</p>
        </motion.div>

        {/* ═══ HEALTH SCORE — dominant hero card ═══ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="rounded-2xl border border-border bg-card shadow-card p-6 sm:p-8 mb-6">
          <div className={`grid grid-cols-1 ${beginnerMode ? "md:grid-cols-2" : "md:grid-cols-3"} gap-6 md:gap-8 items-center`}>
            {/* Score */}
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {bt("Your Readiness Score", t("dash.openingScore"))}
                <BeginnerTooltip tip="This score estimates how prepared you are to open based on your budget, location, and plan completeness. 80+ is great, below 60 means you may have gaps to address." />
              </div>
              <div className={`text-7xl sm:text-8xl font-medium tracking-tight ${scoreColor}`}>
                {r.openingScore}
              </div>
              <div className="text-sm font-medium text-muted-foreground mt-1">{t("dash.outOf100")}</div>
              <div className={`mt-2 text-xs font-medium px-3 py-1 rounded-full ${r.openingScore >= 80 ? "bg-primary/10 text-primary" : r.openingScore >= 60 ? "bg-accent/10 text-accent" : "bg-destructive/10 text-destructive"}`}>
                {beginnerMode
                  ? (r.openingScore >= 80 ? t("dash.greatShape") : r.openingScore >= 60 ? t("dash.fewThings") : t("dash.needsPrep"))
                  : (r.openingScore >= 80 ? t("dash.strongPosition") : r.openingScore >= 60 ? t("dash.needsAttention") : t("dash.highRisk"))
                }
              </div>
            </div>

            {/* Revenue & Budget */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  {bt("How much you'll need to start", "Startup Investment")}
                  <BeginnerTooltip tip="This is the total estimated cost to open your doors — including build-out, equipment, permits, and initial inventory." />
                </div>
                <div className="text-3xl font-medium text-foreground">{cs}{dynamicTotalEstimate.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {bt(
                    `Your budget: ${r.budgetLabel}`,
                    `Budget: ${r.budgetLabel} · Fit: `
                  )}
                  {!beginnerMode && <span className={r.budgetFit === "tight" ? "text-accent font-medium" : "text-primary font-medium"}>{r.budgetFit.charAt(0).toUpperCase() + r.budgetFit.slice(1)}</span>}
                </div>
                {activeNeighborhood && (
                  <div className="text-[11px] text-primary mt-1">
                    Updated for {activeNeighborhood.name} rent (~{cs}{activeNeighborhood.monthlyRentLocal.toLocaleString()}/mo)
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  {bt("Monthly staff costs", "Monthly Labor")}
                  <BeginnerTooltip tip="This is what you'll spend each month on employee wages. It's usually your biggest ongoing cost — try to keep it under 30% of revenue." />
                </div>
                <div className="text-2xl font-medium text-foreground">{cs}{r.totalMonthlyLabor.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {bt(
                    `${r.staffing.filter(s => s.priority === "essential").length} must-have roles`,
                    `${r.staffing.filter(s => s.priority === "essential").length} essential roles · ${r.openingSummary.team}`
                  )}
                </div>
              </div>
            </div>

            {/* Alerts & Timeline — hidden in beginner mode */}
            {!beginnerMode && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                  <Clock className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">{r.timeline}</div>
                    <div className="text-xs text-muted-foreground">~{r.openingSummary.totalWeeks} weeks · {r.timelineSteps.length} phases</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                  <FileCheck className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-foreground">{t("dash.requiredPermits", { count: String(r.permits.filter(p => p.required).length) })}</div>
                    <div className="text-xs text-muted-foreground">{t("dash.checklistItems", { count: String(r.checklist.length) })}</div>
                  </div>
                </div>
                {r.commonMistakes.length > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
                    <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{t("dash.riskFlags", { count: String(r.commonMistakes.length) })}</div>
                      <div className="text-xs text-muted-foreground">{t("dash.founderMistakes")}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Beginner mode: simplified summary below score */}
          {beginnerMode && (
            <div className="mt-6 pt-5 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-lg font-medium text-foreground">{r.timeline}</div>
                <div className="text-xs text-muted-foreground">{t("dash.timeToOpen")}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-medium text-foreground">{r.permits.filter(p => p.required).length}</div>
                <div className="text-xs text-muted-foreground">{t("dash.permitsNeeded")}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-medium text-foreground">{r.staffing.filter(s => s.priority === "essential").length}</div>
                <div className="text-xs text-muted-foreground">{t("dash.rolesToHire")}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-medium text-foreground">{r.checklist.length}</div>
                <div className="text-xs text-muted-foreground">{t("dash.todoItems")}</div>
              </div>
            </div>
          )}
        </motion.div>

        {/* ═══ SURVIVAL SCORE — Studio tier ═══ */}
        {isStudioPlus && <SurvivalScoreCard />}

        {/* ═══ BUDGET ALERT ═══ */}
        <BudgetRealityCheck onboarding={onboarding} realisticTotal={dynamicTotalEstimate} t={t} />

        {/* ═══ ACTIONS — secondary ═══ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex flex-wrap gap-3 mb-8">
          <Button onClick={() => navigate("/plan")} className="gap-2"><FileCheck className="w-4 h-4" /> {t("dash.viewPlan")}</Button>
          {!isFree && (
            <>
              <Button onClick={() => handleExport("en")} disabled={!!downloading} variant="outline" className="gap-2">
                {downloading === "en" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("dash.quickDownload")} (EN)
              </Button>
              <Button onClick={() => handleExport("es")} disabled={!!downloading} variant="outline" className="gap-2">
                {downloading === "es" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("dash.quickDownload")} (ES)
              </Button>
            </>
          )}
          <Button variant="outline" className="gap-2" onClick={() => {
            const shareData = {
              business: r.businessLabel, city: r.city, timeline: r.timeline,
              budget: r.budgetLabel, score: r.openingScore, sqft: r.openingSummary.sqft,
              seats: r.openingSummary.seats, team: r.openingSummary.team,
              serviceStyle: r.openingSummary.serviceStyle, totalEstimate: r.totalEstimate,
              totalMonthlyLabor: r.totalMonthlyLabor,
              staffing: r.staffing.map(s => ({ role: s.role, count: s.count, priority: s.priority })),
              costBreakdown: r.costBreakdown,
              milestones: r.milestones,
              timelineSteps: r.timelineSteps.map(s => ({ step: s.step, weekStart: s.weekStart, weekEnd: s.weekEnd })),
              totalWeeks: r.openingSummary.totalWeeks,
            };
            const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(shareData))));
            const url = `${window.location.origin}/shared?d=${encoded}`;
            navigator.clipboard.writeText(url);
            toast.success("Share link copied — send it to your partner or investor.");
          }}>
            <Share2 className="w-4 h-4" /> {t("dash.shareRoadmap")}
          </Button>
        </motion.div>

        {/* ═══ OPENING SUMMARY — collapsible detail ═══ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          className="bg-card rounded-xl p-5 border border-border shadow-card mb-6 opacity-90">
          <div className="flex items-center gap-2 mb-3">
            <CalendarCheck className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-heading text-sm font-medium text-foreground">{bt("Your Setup Details", t("dash.openingSummary"))}</h3>
            <BeginnerTooltip tip="These are the details you entered during onboarding. They drive all the estimates and recommendations on this page." />
          </div>
          {(() => {
            const isRoomDriven =
              onboarding.businessType === "hotel" ||
              onboarding.businessType === "bed-and-breakfast";
            const cs = getCurrencySymbol(onboarding.city || "");
            const guestRooms = (onboarding.guestRooms ?? []) as Array<{
              type: string; quantity: number; adr: number; occupancy: number;
            }>;
            const totalRooms = guestRooms.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
            const totalAdrWeighted = guestRooms.reduce(
              (s, x) => s + (Number(x.quantity) || 0) * (Number(x.adr) || 0), 0,
            );
            const totalOccWeighted = guestRooms.reduce(
              (s, x) => s + (Number(x.quantity) || 0) * (Number(x.occupancy) || 0), 0,
            );
            const weightedAdr = totalRooms > 0 ? totalAdrWeighted / totalRooms : 0;
            const weightedOcc = totalRooms > 0 ? totalOccWeighted / totalRooms : 0;
            // RevPAR = ADR × occupancy (per available room per night).
            const revPar = weightedAdr * (weightedOcc / 100);

            const items = isRoomDriven && totalRooms > 0
              ? [
                  { label: t("business"), value: r.openingSummary.businessType },
                  { label: t("city"), value: formatCityName(r.openingSummary.city) },
                  { label: t("space"), value: r.openingSummary.sqft },
                  { label: "Total rooms", value: String(totalRooms), highlight: true },
                  { label: "ADR", value: `${cs}${Math.round(weightedAdr).toLocaleString()}` },
                  { label: "Occupancy", value: `${Math.round(weightedOcc)}%` },
                  { label: "RevPAR", value: `${cs}${Math.round(revPar).toLocaleString()}`, highlight: true },
                  { label: t("service"), value: r.openingSummary.serviceStyle },
                  { label: t("daysWeek"), value: `${r.openingSummary.daysPerWeek} days` },
                ]
              : [
                  { label: t("business"), value: r.openingSummary.businessType },
                  { label: t("city"), value: formatCityName(r.openingSummary.city) },
                  { label: t("space"), value: r.openingSummary.sqft },
                  { label: "Indoor seats", value: (r.openingSummary as any).indoorSeats > 0 ? String((r.openingSummary as any).indoorSeats) : "—" },
                  { label: "Outdoor seats", value: (r.openingSummary as any).outdoorSeats > 0 ? String((r.openingSummary as any).outdoorSeats) : "—" },
                  { label: "Bar seats", value: (r.openingSummary as any).barSeats > 0 ? String((r.openingSummary as any).barSeats) : "—" },
                  { label: "Total capacity", value: (r.openingSummary as any).totalCapacity > 0 ? String((r.openingSummary as any).totalCapacity) : (r.openingSummary.seats > 0 ? `${r.openingSummary.seats}` : "N/A"), highlight: true },
                  { label: t("service"), value: r.openingSummary.serviceStyle },
                  { label: t("daysWeek"), value: `${r.openingSummary.daysPerWeek} days` },
                ];

            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {items.map(item => (
                  <div key={item.label}>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</div>
                    <div className={`text-sm font-medium mt-0.5 ${(item as any).highlight ? "text-primary font-medium" : "text-foreground"}`}>{item.value}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </motion.div>

        {/* ═══ TIMELINE — secondary ═══ */}
        {isFree ? (
          <div className="mb-6">
            <LockedSection title="Full Timeline — Pro plan" description="Upgrade to see every phase of your opening mapped out week by week." requiredPlan="Pro">
              <div className="bg-card rounded-xl p-5 border border-border shadow-card">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-heading text-sm font-medium text-foreground">{t("dash.weekByWeek")}</h3>
                </div>
                <div className="space-y-1">
                  {r.timelineSteps.slice(0, 3).map((phase, i) => {
                    const maxEnd = Math.max(r.openingSummary.totalWeeks || 0, ...r.timelineSteps.map(p => p.weekEnd), 1);
                    const start = Math.max(0, Math.min(phase.weekStart, maxEnd));
                    const end = Math.max(start, Math.min(phase.weekEnd, maxEnd));
                    const leftPct = (start / maxEnd) * 100;
                    const widthPct = Math.max(((end - start) / maxEnd) * 100, 3);
                    const clampedWidth = Math.min(widthPct, 100 - leftPct);
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-32 sm:w-40 text-xs text-muted-foreground truncate shrink-0">{phase.step}</div>
                        <div className="flex-1 h-6 bg-muted/50 rounded relative overflow-hidden">
                          <div className="absolute top-0.5 bottom-0.5 rounded bg-primary" style={{ left: `${leftPct}%`, width: `${clampedWidth}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="h-6 bg-muted/30 rounded" />
                  <div className="h-6 bg-muted/20 rounded" />
                </div>
              </div>
            </LockedSection>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-card rounded-xl p-5 border border-border shadow-card mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-heading text-sm font-medium text-foreground">{t("dash.weekByWeek")}</h3>
            </div>
            {(() => {
              const maxEnd = Math.max(r.openingSummary.totalWeeks || 0, ...r.timelineSteps.map(p => p.weekEnd), 1);
              return (
                <>
                  <div className="space-y-1">
                    {r.timelineSteps.map((phase, i) => {
                      const start = Math.max(0, Math.min(phase.weekStart, maxEnd));
                      const end = Math.max(start, Math.min(phase.weekEnd, maxEnd));
                      const leftPct = (start / maxEnd) * 100;
                      const rawWidthPct = Math.max(((end - start) / maxEnd) * 100, 3);
                      const widthPct = Math.min(rawWidthPct, 100 - leftPct);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-32 sm:w-40 text-xs text-muted-foreground truncate shrink-0">{phase.step}</div>
                          <div className="w-12 sm:w-14 text-[10px] text-muted-foreground tabular-nums shrink-0 text-right">
                            W{phase.weekStart}–{phase.weekEnd}
                          </div>
                          <div className="flex-1 min-w-0 h-6 bg-muted/50 rounded relative overflow-hidden">
                            <div
                              className={`absolute top-0.5 bottom-0.5 rounded ${phase.done ? "bg-primary/30" : "bg-primary"}`}
                              style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Week 0</span>
                    <span>Week {maxEnd}</span>
                  </div>
                </>
              );
            })()}
          </motion.div>
        )}

        {/* ═══ STAFFING & SPACE — secondary ═══ */}
        {isFree ? (
          <div className="mb-6">
            <LockedSection title="Staffing & Space Analysis — Pro plan" description="Get role-by-role hiring recommendations and space layout guidance tailored to your concept." requiredPlan="Pro">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card rounded-xl p-5 border border-border shadow-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-heading text-sm font-medium text-foreground">{t("dash.estimatedStaffing")}</h3>
                  </div>
                  <div className="space-y-3">
                    {r.staffing.slice(0, 3).map((role, i) => (
                      <div key={i} className="flex items-start justify-between gap-2">
                        <span className="text-sm text-foreground">{role.role}</span>
                        <span className="text-sm text-muted-foreground">{role.hourlyRate}</span>
                      </div>
                    ))}
                    <div className="h-4 bg-muted/30 rounded" />
                    <div className="h-4 bg-muted/20 rounded" />
                  </div>
                </div>
                <div className="bg-card rounded-xl p-5 border border-border shadow-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Ruler className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-heading text-sm font-medium text-foreground">{t("dash.spaceGuidance")}</h3>
                  </div>
                  <div className="h-4 bg-muted/30 rounded mb-2" />
                  <div className="h-4 bg-muted/20 rounded" />
                </div>
              </div>
            </LockedSection>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 mb-6">

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
              className="bg-card rounded-xl p-5 border border-border shadow-card">
              <div className="flex items-center gap-2 mb-3">
                <Ruler className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-heading text-sm font-medium text-foreground">{bt("How much space you need", t("dash.spaceGuidance"))}</h3>
                <BeginnerTooltip tip="Based on your business type and seating needs, this is the range of square footage that works best. Too small and you'll be cramped; too big and rent eats your profits." />
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{bt("Recommended size", t("dash.recommendedSqft"))}</div>
                  <div className="text-xl font-medium text-foreground">{formatAreaRange(r.recommendedSqft.min, r.recommendedSqft.max, onboarding.city)}</div>
                  <p className="text-xs text-muted-foreground mt-1">{r.recommendedSqft.note}</p>
                </div>
                {r.openingSummary.seats > 0 && (() => {
                  const totalSqft = onboarding.sqftMax || onboarding.sqftMin || r.recommendedSqft.max;
                  const seatingType = (onboarding.seatingType || "indoor") as "indoor" | "outdoor" | "both";
                  const capacity = calculateSeatingCapacity(onboarding.businessType, totalSqft, seatingType);
                  const seatLabel = getSeatingLabel(onboarding.businessType);
                  return (
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Armchair className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-foreground">Seating Capacity</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {capacity.indoorSeats > 0 && (
                          <div>
                            <div className="text-lg font-medium text-foreground">{capacity.indoorSeats}</div>
                            <div className="text-xs text-muted-foreground">Indoor {seatLabel}</div>
                          </div>
                        )}
                        {capacity.outdoorSeats > 0 && (
                          <div>
                            <div className="text-lg font-medium text-foreground">{capacity.outdoorSeats}</div>
                            <div className="text-xs text-muted-foreground">Outdoor {seatLabel}</div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{capacity.note}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Startup Cost Breakdown — hidden in beginner mode */}
              {!beginnerMode && (
              <div className="mt-5 pt-4 border-t border-border">
                <h4 className="text-xs font-medium text-foreground uppercase tracking-wider mb-3">{t("dash.startupCost")}</h4>
                <div className="space-y-1.5">
                  {r.costBreakdown.map(c => (
                    <div key={c.category} className="flex items-center gap-2">
                      <div className="flex-1 text-xs text-muted-foreground">{c.category}</div>
                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${c.percentage}%` }} />
                      </div>
                      <div className="text-xs font-medium text-foreground w-16 text-right">{cs}{c.estimate.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-sm font-medium">
                  <span className="text-foreground">{t("dash.total")}</span>
                  <span className="text-primary">{cs}{r.totalEstimate.toLocaleString()}</span>
                </div>
              </div>
              )}
            </motion.div>
          </div>
        )}

        {!isFree && <StaffingControl />}

        {/* ═══ MILESTONES — secondary ═══ */}
        {isFree ? (
          <div className="mb-6">
            <LockedSection title="Opening Milestones — Pro plan" description="See every critical milestone mapped to your opening date so nothing falls through the cracks." requiredPlan="Pro">
              <div className="bg-card rounded-xl p-5 border border-border shadow-card">
                <div className="flex items-center gap-2 mb-4">
                  <Flag className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-heading text-sm font-medium text-foreground">{t("dash.milestones")}</h3>
                </div>
                <div className="space-y-3">
                  {r.milestones.slice(0, 3).map((m, i) => (
                    <div key={i} className="flex items-center gap-4 pl-1">
                      <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
                      <span className="text-sm text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                  <div className="h-5 bg-muted/20 rounded" />
                  <div className="h-5 bg-muted/15 rounded" />
                </div>
              </div>
            </LockedSection>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-card rounded-xl p-5 border border-border shadow-card mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Flag className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-heading text-sm font-medium text-foreground">{bt("Key deadlines", t("dash.milestones"))}</h3>
              <BeginnerTooltip tip="These are the big deadlines you need to hit before opening day. The ones with a green dot are the most critical — don't miss them." />
            </div>
            <div className="relative">
              <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-border" />
              <div className="space-y-2.5">
                {(beginnerMode ? r.milestones.filter(m => m.critical) : r.milestones).map((m, i) => (
                  <div key={i} className="flex items-center gap-4 pl-1">
                    <div
                      className={`w-2.5 h-2.5 rounded-full shrink-0 z-10 ${m.critical ? "bg-primary" : "bg-muted-foreground/40"}`}
                    />
                    <div className="flex-1 flex items-center justify-between gap-3">
                      <span className={`text-sm ${m.critical ? "font-medium text-foreground" : "text-muted-foreground"}`}>{m.label}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{m.weeksBeforeOpen === 0 ? t("dash.openingDay") : `${m.weeksBeforeOpen}w ${t("dash.before")}`}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ═══ QUICK NAV — 8 cards, locked ones greyed with lock icon ═══ */}
        {(() => {
          type CardDef = {
            label: string;
            icon: typeof MapPin;
            path: string;
            desc?: string;
            requiredPlan: PlanType; // minimum plan to access
          };
          // Starter is collapsed to Free (deprecated tier).
          const planRank: Record<PlanType, number> = { free: 0, starter: 0, pro: 1, business: 2, enterprise: 3 };
          const userRank = planRank[plan];

          const cards: CardDef[] = [
            // Free tier
            { label: bt("Your timeline", t("dash.openingTimeline")), icon: Clock, path: "/timeline", requiredPlan: "free" },
            { label: bt("To-do list", t("dash.checklist")), icon: CheckSquare, path: "/checklist", requiredPlan: "free" },
            // Free tier (was Starter — Starter plan removed; these features stayed Free)
            { label: bt("Money plan", t("dash.budgetPlanner")), icon: DollarSign, path: "/budget", requiredPlan: "free" },
            { label: bt("Permits & licenses", t("dash.permits")), icon: FileCheck, path: "/permits", desc: `${r.permits.filter(p => p.required).length} required`, requiredPlan: "free" },
            // Pro tier
            { label: t("dash.forecast"), icon: BarChart3, path: "/forecast", requiredPlan: "pro" },
            { label: bt("Find a spot", t("dash.locations")), icon: MapPin, path: "/locations", desc: beginnerMode ? "Browse neighborhoods" : `${r.neighborhoods.length} areas`, requiredPlan: "pro" },
            { label: "Equipment", icon: Package2, path: "/equipment", requiredPlan: "pro" },
            { label: "Staff Rota", icon: Users, path: "/staff-rota", requiredPlan: "pro" },
            { label: "Operations", icon: Sparkles, path: "/operations", desc: "Daily revenue · menu · waste", requiredPlan: "pro" },
          ];

          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              {cards.map((card, i) => {
                const locked = userRank < planRank[card.requiredPlan];
                const planForModal = (card.requiredPlan === "free" || card.requiredPlan === "starter")
                  ? "pro"
                  : (card.requiredPlan as "pro" | "business");
                return (
                  <motion.button
                    key={card.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.03 }}
                    onClick={() => {
                      if (locked) {
                        setFeatureModal({ open: true, name: card.label, plan: planForModal });
                      } else {
                        navigate(card.path);
                      }
                    }}
                    aria-label={locked ? `${card.label} (locked — requires ${card.requiredPlan} plan)` : card.label}
                    className={`relative bg-card rounded-lg p-4 border border-border text-left group transition-all ${
                      locked
                        ? "opacity-60 hover:opacity-80 hover:border-primary/40 cursor-pointer"
                        : "hover:shadow-elevated"
                    }`}
                  >
                    {locked && (
                      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-muted-foreground">
                        <Lock className="w-3 h-3" />
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${locked ? "bg-muted/60" : "bg-muted"}`}>
                        <card.icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      {!locked && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" />}
                    </div>
                    <h3 className="text-sm font-medium text-foreground pr-5">{card.label}</h3>
                    {card.desc && !locked && <p className="text-xs text-muted-foreground mt-0.5">{card.desc}</p>}
                    {locked && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 capitalize">
                        {card.requiredPlan} plan
                      </p>
                    )}
                  </motion.button>
                );
              })}
            </div>
          );
        })()}

        {/* ═══ COMMON MISTAKES & FIRST 7 DAYS — tertiary, compact ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="bg-card rounded-xl p-5 border border-border">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-accent" />
              <h3 className="font-heading text-sm font-medium text-foreground">{bt("Things to watch out for", t("dash.commonMistakes"))}</h3>
              <BeginnerTooltip tip="These are the most common mistakes first-time founders make. Read through them now so you can avoid them later." />
            </div>
            <ul className="space-y-1.5">
              {r.commonMistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <span className="text-accent mt-0.5 shrink-0">•</span>
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}
            className="bg-card rounded-xl p-5 border border-border">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-primary" />
              <h3 className="font-heading text-sm font-medium text-foreground">{bt("Your first week checklist", t("dash.first7Days"))}</h3>
              <BeginnerTooltip tip="Once you open, these are the most important things to focus on during your very first week of business." />
            </div>
            <ul className="space-y-1.5">
              {r.first7Days.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
        </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-8 border border-border shadow-card text-center">
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="font-heading text-xl font-medium text-foreground mb-2">
              Welcome, {businessDisplayName}!
            </h3>
            <p className="text-muted-foreground mb-6">
              Your dashboard is ready. Generate your personalized opening roadmap to get permits, costs, timelines, and location insights — all in one place.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={() => navigate("/onboarding")} className="gap-2">
                <Sparkles className="w-4 h-4" /> Build My Roadmap
              </Button>
              <Button variant="outline" onClick={() => navigate("/assistant")} className="gap-2">
                <MessageCircle className="w-4 h-4" /> Ask a Question
              </Button>
            </div>
          </motion.div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
